/* eslint-disable max-lines */
import { fetchEnvelopeRecipientsAggs } from 'actions';
import { changeRecipientFilter } from 'actions/envelopeView';
import fileExtensionIconParser from 'constants/fileIcons';
import { platformIcon } from 'constants/platformIcons';
import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getCustomer } from 'selectors/auth';
import { getSelectedCommunication } from 'selectors/communications';
import {
  getContextModels,
  getCustodians,
  getEnvelope,
  getEnvelopeMetadata,
  getRecipientsFromEnvelope,
} from 'selectors/envelopes';
import { getRecipientFilter, getRecipientFilterCounts } from 'selectors/envelopeView';
import { useSelector } from 'store';
import { isFileCommunication } from 'utils/communications';

const EnvelopeContextInfo: React.FC = () => {
  const { envelopeId } = useParams<{ envelopeId: string }>();
  const dispatch = useDispatch();

  const envelope = useSelector((state) => getEnvelope(state, envelopeId));

  const custodians = useSelector(getCustodians);
  const contextModels: string[] = useSelector(getContextModels);
  const metadata: string[] = useSelector((state) => getEnvelopeMetadata(state, envelopeId));
  const recipientFilter = useSelector(getRecipientFilter);
  const recipientFilterCounts = useSelector(getRecipientFilterCounts);
  const recipients = useSelector((state) => getRecipientsFromEnvelope(state, envelopeId));

  const communication = useSelector(getSelectedCommunication);
  const customer = useSelector(getCustomer);

  const communcationRetentionDays = customer.config?.communication_retention_days || 0;

  const isFile = isFileCommunication(communication);

  const extension =
    envelope.communications?.[0].meta_data?.filename?.split('/').pop()?.split('.').pop() || '';

  useEffect(() => {
    const cleanRecipients = recipients.reduce<string[]>(
      (acc, r) => (r.user_uuid ? [...acc, r.user_uuid] : acc),
      []
    );
    const recipientsWithSender = [
      ...new Set([
        ...cleanRecipients,
        ...(envelope.created_by_uuid ? [envelope.created_by_uuid] : []),
      ]),
    ];

    dispatch(
      fetchEnvelopeRecipientsAggs({
        recipientsIds: recipientsWithSender,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleRecipientFilterChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (recipientFilter.includes(e.target.name)) {
      dispatch(changeRecipientFilter(recipientFilter.filter((r) => r !== e.target.name)));
    } else {
      dispatch(changeRecipientFilter([...recipientFilter, e.target.name]));
    }
  };

  const handleClear = (): void => {
    dispatch(changeRecipientFilter([]));
  };

  return (
    <div className="envelope-info-container text-small gap-4">
      {envelope && (
        <>
          <div className="flex flex-col gap-2">
            <div className="flex flex-row justify-between">
              <span className="font-bold">{`${!isFile ? 'Message' : 'File'} Metadata`}</span>
              <span className="w-4 h-4">
                {!isFile
                  ? platformIcon(envelope.platform, envelope.integration_type, true)
                  : fileExtensionIconParser(extension)}
              </span>
            </div>
            <div>
              <ul className="list-disc list-outside pl-6">
                {custodians.length > 0 &&
                  custodians.map((custodian) => (
                    <>
                      <li key={custodian.name}>{custodian.name}</li>
                      <ul className="list-disc list-outside pl-6">
                        {custodian.folders.map((folder) => (
                          <li key={`${custodian.name}-${folder}`}>{folder}</li>
                        ))}
                      </ul>
                    </>
                  ))}

                {metadata.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </div>
          </div>

          {contextModels.length > 0 && (
            <div className="flex flex-col gap-2">
              <div className="flex flex-row justify-between">
                <span className="font-bold">Context Models</span>
                {envelope.platform === 'o365_teams' &&
                  platformIcon(envelope.platform, envelope.integration_type, true)}
              </div>
              <div>
                <ul className="list-disc list-outside pl-6">
                  {contextModels.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2.5 text-small">
            <div className="flex flex-row justify-between">
              <span className="font-bold">
                Past {communcationRetentionDays} days (In Retention)
              </span>
              <button
                type="button"
                className="text-litlingo-primary underline focus:outline-none"
                onClick={handleClear}
              >
                Clear All
              </button>
            </div>
            <div className="flex flex-col gap-0.5">
              <div className="ml-1">
                <label
                  htmlFor="off-radar-checkbox"
                  className="flex flex-row gap-2 items-center"
                  key="off-radar"
                >
                  <input
                    id="off-radar-checkbox"
                    type="checkbox"
                    name="off-radar"
                    className="form-checkbox litlingo-checkbox h-4 w-4 transition duration-150 ease-in-out"
                    checked={recipientFilter.includes('off-radar')}
                    onChange={handleRecipientFilterChange}
                    disabled={recipientFilterCounts['off-radar'] === 0}
                  />
                  <span>{recipientFilterCounts['off-radar']} recipients off radar, on risk</span>
                </label>
              </div>

              <div className="ml-1">
                <label
                  htmlFor="higher-risk-checkbox"
                  className="flex flex-row gap-2 items-center"
                  key="higher-risk"
                >
                  <input
                    id="higher-risk-checkbox"
                    type="checkbox"
                    name="higher-risk"
                    className="form-checkbox litlingo-checkbox h-4 w-4 transition duration-150 ease-in-out"
                    checked={recipientFilter.includes('higher-risk')}
                    onChange={handleRecipientFilterChange}
                    disabled={recipientFilterCounts['higher-risk'] === 0}
                  />
                  <span>{recipientFilterCounts['higher-risk']} higher risk sender</span>
                </label>
              </div>

              <div className="ml-1">
                <label
                  htmlFor="external-domains-checkbox"
                  className="flex flex-row gap-2 items-center"
                  key="external-domains"
                >
                  <input
                    id="external-domains-checkbox"
                    type="checkbox"
                    name="external-domains"
                    className="form-checkbox litlingo-checkbox h-4 w-4 transition duration-150 ease-in-out"
                    checked={recipientFilter.includes('external-domains')}
                    onChange={handleRecipientFilterChange}
                    disabled={recipientFilterCounts['external-domains'] === 0}
                  />
                  <span>{recipientFilterCounts['external-domains']} non company domains</span>
                </label>
              </div>

              <div className="ml-1">
                <label
                  htmlFor="after-hours-checkbox"
                  className="flex flex-row gap-2 items-center"
                  key="after-hours"
                >
                  <input
                    id="after-hours-checkbox"
                    type="checkbox"
                    name="after-hours"
                    className="form-checkbox litlingo-checkbox h-4 w-4 transition duration-150 ease-in-out"
                    checked={recipientFilter.includes('after-hours')}
                    onChange={handleRecipientFilterChange}
                    disabled={recipientFilterCounts['after-hours'] === 0}
                  />
                  <span>
                    {recipientFilterCounts['after-hours']} after-hours high risk communication
                  </span>
                </label>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EnvelopeContextInfo;
